import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import "./ChartFiltersPanel.css";

/**
 * Props:
 *  - employees: Array<
 *      string |
 *      { value?: string|number, id?: string|number, username?: string, email?: string,
 *        name?: string, label?: string }
 *    >
 *  - categories?: string[]                 // optional; hide Category UI if omitted/empty
 *  - hideCategories?: boolean              // force-hide category UI when true
 *  - defaultScope?: "aggregate" | "granular"
 *  - defaultEmployees?: string[]
 *  - defaultCategories?: string[]
 *  - onApply?: ({ scope, employees, categories }) => void
 *  - getPeriod?: () => string
 *  - chartPeriodSelector?: string          // CSS selector of the period label
 *  - storageKey?: string                   // key to persist filter state in sessionStorage
 *  - onCollapsedChange?: (collapsed: boolean) => void
 */

const STORAGE_PREFIX = "tmChartFilters:";

function loadStoredState(storageKey) {
  if (!storageKey) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.warn("[ChartFiltersPanel] Failed to load stored filters", e);
    return null;
  }
}

function saveStoredState(storageKey, state) {
  if (!storageKey) return;
  try {
    sessionStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(state));
  } catch (e) {
    console.warn("[ChartFiltersPanel] Failed to save filters", e);
  }
}

function clearStoredState(storageKey) {
  if (!storageKey) return;
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + storageKey);
  } catch (e) {
    console.warn("[ChartFiltersPanel] Failed to clear stored filters", e);
  }
}

export default function ChartFiltersPanel({
  employees = [],
  categories = undefined,
  hideCategories = false,
  defaultScope = "aggregate",
  defaultEmployees = [],
  defaultCategories = [],
  onApply = () => {},
  getPeriod,
  chartPeriodSelector = ".chart-period",
  storageKey,
  onCollapsedChange,
}) {
  // Load stored filters (if any) once on mount
  const initialStored =
    typeof window !== "undefined" ? loadStoredState(storageKey) : null;

  const [scope, setScope] = useState(initialStored?.scope ?? defaultScope);
  const [selEmployees, setSelEmployees] = useState(
    initialStored?.employees ?? defaultEmployees
  );
  const [selCategories, setSelCategories] = useState(
    initialStored?.categories ?? defaultCategories
  );

  // collapsed / minimized state (internal)
  const [collapsed, setCollapsed] = useState(false);

  // 🔗 Tell parent whenever collapsed changes (including first render)
  useEffect(() => {
    if (typeof onCollapsedChange === "function") {
     // console.log("[ChartFiltersPanel] Collapsed changed to", collapsed);
      onCollapsedChange(collapsed);
    }
  }, [collapsed, onCollapsedChange]);

  // Only show categories if:
  //  - we have a non-empty array AND
  //  - hideCategories is NOT true
  const hasCategories =
    !hideCategories && Array.isArray(categories) && categories.length > 0;

  // Normalize employees: { value, label, searchable }
  const normEmployees = useMemo(
    () =>
      employees.map((e) =>
        typeof e === "string"
          ? {
              value: e,
              label: e,
              searchable: e.toLowerCase(),
            }
          : {
              value: String(e.value ?? e.id ?? e.username ?? e.email ?? ""),
              label: String(
                e.name ??
                  e.label ??
                  e.username ??
                  e.email ??
                  e.value ??
                  e.id ??
                  ""
              ),
              searchable: String(
                e.name ?? e.label ?? e.username ?? e.email ?? ""
              ).toLowerCase(),
            }
      ),
    [employees]
  );

  const ids = useId();
  const scopeAggId = `${ids}-agg`;
  const scopeGranId = `${ids}-gran`;
  const empId = `${ids}-emps`;
  const catId = `${ids}-cats`;

  const getPeriodLabel = () => {
    if (typeof getPeriod === "function") return getPeriod();
    const now = new Date();
    return now.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
  };

  const reset = () => {
    setScope(defaultScope);
    setSelEmployees([]);
    setSelCategories([]);
    clearStoredState(storageKey);

    const el = document.querySelector(chartPeriodSelector);
    if (el) el.textContent = getPeriodLabel();
  };

  // ✅ Always can apply now (no forced selections)
  const canApply = true;

  const compactList = (arr, max = 3) =>
    arr.length <= max
      ? arr.join(", ")
      : `${arr.slice(0, max).join(", ")} +${arr.length - max}`;

  const apply = () => {
    const payload = {
      scope,
      employees: scope === "granular" ? selEmployees : [],
      categories:
        scope === "granular" && hasCategories ? selCategories : null,
    };
    onApply(payload);

    // Update period + filter summary in .chart-period
    const labelByValue = new Map(normEmployees.map((e) => [e.value, e.label]));
    const period = getPeriodLabel();
    let suffix = "";

    if (scope === "aggregate") {
      suffix = " — Aggregate";
    } else {
      // Granular summary
      let employeeLabels = selEmployees.map((v) => labelByValue.get(v) ?? v);
      if (employeeLabels.length === 0) {
        employeeLabels = ["All employees"];
      }

      const parts = [`Employees: ${compactList(employeeLabels)}`];

      if (hasCategories) {
        const cats =
          selCategories.length > 0 ? selCategories : ["All categories"];
        parts.push(`Categories: ${compactList(cats)}`);
      }

      suffix = ` — ${parts.join(" | ")}`;
    }

    const el = document.querySelector(chartPeriodSelector);
    if (el) el.textContent = `${period}${suffix}`;
  };

  // === native multi-select helpers
  const readMulti = (e) =>
    Array.from(e.target.selectedOptions).map((o) => o.value);
  const selectAllEmployees = () =>
    setSelEmployees(normEmployees.map((e) => e.value));
  const clearEmployees = () => setSelEmployees([]);
  const selectAllCategories = () =>
    hasCategories && setSelCategories([...categories]);
  const clearCategories = () => setSelCategories([]);

  // === Popup (Modal) for Employees ===
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [empQuery, setEmpQuery] = useState("");
  const [modalSelection, setModalSelection] = useState(selEmployees);

  const openEmpModal = () => {
    setEmpQuery("");
    setModalSelection(selEmployees);
    setShowEmpModal(true);
  };

  const closeEmpModal = () => setShowEmpModal(false);

  const saveEmpModal = () => {
    setSelEmployees(modalSelection);
    setShowEmpModal(false);
  };

  const filteredEmployees = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    if (!q) return normEmployees;
    return normEmployees.filter((e) => e.searchable.includes(q));
  }, [empQuery, normEmployees]);

  const modalAllVisibleVals = filteredEmployees.map((e) => e.value);
  const toggleModalItem = (value) =>
    setModalSelection((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  const modalSelectAllVisible = () =>
    setModalSelection((prev) =>
      Array.from(new Set([...prev, ...modalAllVisibleVals]))
    );
  const modalClearVisible = () =>
    setModalSelection((prev) =>
      prev.filter((v) => !modalAllVisibleVals.includes(v))
    );

  const modalRef = useRef(null);
  useEffect(() => {
    if (!showEmpModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowEmpModal(false);
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => {
      modalRef.current?.querySelector("input[type='search']")?.focus();
    }, 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [showEmpModal]);

  // Persist filters whenever they change
  useEffect(() => {
    saveStoredState(storageKey, {
      scope,
      employees: selEmployees,
      categories: selCategories,
    });
  }, [scope, selEmployees, selCategories, storageKey]);

  return (
    <div className="cf-card" role="region" aria-label="Chart Filters">
      {/* Header with minimize / expand toggle */}
      <div className="cf-header">
        <h2 className="cf-title">Chart Filters</h2>
        <button
          type="button"
          className="cf-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand chart filters" : "Collapse chart filters"}
        >
          {collapsed ? "Show Filters" : "Hide Filters"}
        </button>
      </div>

      {!collapsed && (
        <>
          <hr className="cf-sep" />

          {/* Scope */}
          <fieldset className="cf-fieldset">
            <legend className="cf-legend">Chart Scope</legend>
            <div className="cf-radio-row">
              <label htmlFor={scopeAggId} className="cf-radio">
                <input
                  id={scopeAggId}
                  type="radio"
                  name={`${ids}-scope`}
                  value="aggregate"
                  checked={scope === "aggregate"}
                  onChange={() => setScope("aggregate")}
                />
                <span>Aggregate</span>
              </label>
              <label htmlFor={scopeGranId} className="cf-radio">
                <input
                  id={scopeGranId}
                  type="radio"
                  name={`${ids}-scope`}
                  value="granular"
                  checked={scope === "granular"}
                  onChange={() => setScope("granular")}
                />
                <span>Granular</span>
              </label>
            </div>
          </fieldset>

          {/* Granular-only controls */}
          {scope === "granular" && (
            <>
              <div className="cf-group">
                <div className="cf-label-row">
                  <div className="cf-label">Employees</div>
                  <div className="cf-mini-actions">
                    <button
                      type="button"
                      className="cf-link"
                      onClick={selectAllEmployees}
                    >
                      Select all
                    </button>
                    <span aria-hidden="true">·</span>
                    <button
                      type="button"
                      className="cf-link"
                      onClick={clearEmployees}
                    >
                      Clear
                    </button>
                    <span aria-hidden="true">·</span>
                    <button
                      type="button"
                      className="cf-link cf-link-strong"
                      onClick={openEmpModal}
                      aria-haspopup="dialog"
                    >
                      Browse employees…
                    </button>
                  </div>
                </div>

                <label className="sr-only" htmlFor={empId}>
                  Choose employees
                </label>
                <select
                  id={empId}
                  className="cf-multiselect"
                  multiple
                  size={Math.min(8, Math.max(4, normEmployees.length || 4))}
                  value={selEmployees}
                  onChange={(e) => setSelEmployees(readMulti(e))}
                >
                  {normEmployees.map((e) => (
                    <option key={e.value} value={e.value}>
                      {e.label}
                    </option>
                  ))}
                </select>
                <div className="cf-hint">
                  Selected: <strong>{selEmployees.length}</strong> — use
                  “Browse employees…” to search and pick from the full list.
                </div>
              </div>

              {hasCategories && (
                <div className="cf-group">
                  <div className="cf-label-row">
                    <div className="cf-label">Categories</div>
                    <div className="cf-mini-actions">
                      <button
                        type="button"
                        className="cf-link"
                        onClick={selectAllCategories}
                      >
                        Select all
                      </button>
                      <span aria-hidden="true">·</span>
                      <button
                        type="button"
                        className="cf-link"
                        onClick={clearCategories}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <label className="sr-only" htmlFor={catId}>
                    Choose categories
                  </label>
                  <select
                    id={catId}
                    className="cf-multiselect"
                    multiple
                    size={Math.min(8, Math.max(4, categories.length || 4))}
                    value={selCategories}
                    onChange={(e) => setSelCategories(readMulti(e))}
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          <hr className="cf-sep" />

          <div className="cf-footer">
            <button type="button" className="cf-secondary" onClick={reset}>
              Reset
            </button>
            <button
              type="button"
              className="cf-primary"
              onClick={apply}
              disabled={!canApply}
              title={canApply ? "Apply filters" : "Complete required selections"}
            >
              Apply Filters
            </button>
          </div>
        </>
      )}

      {/* EMPLOYEE BROWSE MODAL */}
      {showEmpModal && (
        <div
          className="cf-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Browse employees"
        >
          <div className="cf-modal" ref={modalRef}>
            <div className="cf-modal-header">
              <h3 className="cf-modal-title">Browse employees</h3>
              <button
                className="cf-modal-close"
                onClick={closeEmpModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="cf-modal-toolbar">
              <input
                type="search"
                placeholder="Search employees…"
                value={empQuery}
                onChange={(e) => setEmpQuery(e.target.value)}
                className="cf-search"
              />
              <div className="cf-modal-actions">
                <button
                  type="button"
                  className="cf-chip"
                  onClick={modalSelectAllVisible}
                >
                  Select visible
                </button>
                <button
                  type="button"
                  className="cf-chip"
                  onClick={modalClearVisible}
                >
                  Clear visible
                </button>
              </div>
            </div>

            <div
              className="cf-modal-list"
              role="listbox"
              aria-label="Employee list"
            >
              {filteredEmployees.length === 0 ? (
                <div className="cf-empty">
                  No employees match “{empQuery}”.
                </div>
              ) : (
                filteredEmployees.map((e) => {
                  const checked = modalSelection.includes(e.value);
                  return (
                    <label key={e.value} className="cf-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleModalItem(e.value)}
                      />
                      <span className="cf-row-label">{e.label}</span>
                    </label>
                  );
                })
              )}
            </div>

            <div className="cf-modal-footer">
              <div className="cf-count">
                Selected: <strong>{modalSelection.length}</strong>
              </div>
              <div className="cf-modal-buttons">
                <button className="cf-secondary" onClick={closeEmpModal}>
                  Cancel
                </button>
                <button
                  className="cf-primary"
                  onClick={saveEmpModal}
                  disabled={modalSelection.length === 0}
                >
                  Save selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cf-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem; 
        }
        .cf-toggle {
          border: none;
          background: transparent;
          font-size: 0.85rem;
          cursor: pointer;
          text-decoration: underline;
          padding: 4px 6px;
        }
      `}</style>
    </div>
  );
}
